import { useMemo, useCallback, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useEvaluationContext } from '../../../runtime/conditions/use-evaluation-context';
import { resolveTKey } from '../../../i18n/resolve-key';
import { useI18nDebugContext } from '../../../i18n/use-i18n-debug-context';
import { useTableData } from './use-table-data.ts';
import { useSavedViews } from './use-saved-views.ts';
import { useLocalDataSource, type TableDataSource } from './table-data-source.js';
import { useTableActions } from './use-table-actions.ts';
import { useTableSelection } from './use-table-selection.ts';
import { useTableExport } from './use-table-export.ts';
import type { TableSortState, TableFilterState } from './table-state.js';
import { createSavedViewStatePatch, getGlobalDefaultPageSize, getVisibleOrderedColumns } from './table-state.js';
import { usePreferencesPageSize } from './use-preferences-page-size.ts';
import { getColumnIntrinsicMinWidth } from './column-sizing.js';
import { tableTokens } from './table-tokens.js';
import type {
  TableRendererProps,
  TableSavedViewOption,
  TableActionContext,
} from './table-renderer-props.ts';
import type { ActionDescriptor } from '@finance/contracts';
import type { ResolvedAction } from '../../../runtime/actions/action-resolution';
import type { EvaluationContext } from '../../../runtime/conditions/evaluate-condition';

export interface UseTableRendererResult {
  contract: TableRendererProps['contract'];
  evalCtx: EvaluationContext;
  resolvedLocale: NonNullable<TableRendererProps['locale']>;
  state: ReturnType<typeof useTableData>['state'];
  tableData: Record<string, unknown>[];
  totalCount: number;
  aggregates: ReturnType<typeof useTableData>['aggregates'];
  groupMetadata: ReturnType<typeof useTableData>['groupMetadata'];
  referenceDisplayMap: ReturnType<typeof useTableData>['referenceDisplayMap'];
  actions: ReturnType<typeof useTableData>['actions'];

  displayLoading: boolean;
  displayError: Error | null | undefined;
  displayStale: boolean;
  displayDegraded: boolean;

  showFilters: boolean;
  setShowFilters: (value: boolean | ((prev: boolean) => boolean)) => void;

  savedViews: TableSavedViewOption[];
  currentViewId: string | null;
  pageSizeOptions: number[];

  rowActivationAction: ActionDescriptor | undefined;
  selectionEnabled: boolean;
  allPageSelected: boolean;
  indeterminate: boolean;
  pinnedLeft: string[];
  pinnedRight: string[];
  showPagination: boolean;
  tableMinWidth: number;

  handleSortChange: (sorting: TableSortState[]) => void;
  handleFilterSet: (filter: TableFilterState) => void;
  handleFilterRemove: (field: string) => void;
  handleGlobalSearchChange: (search: string) => void;
  handleToggleFilterRow: () => void;
  handleDensityChange: (density: 'compact' | 'standard' | 'comfortable') => void;
  handleGroupingChange: (grouping: ReturnType<typeof useTableData>['state']['grouping']) => void;
  handleAggregationsChange: (
    aggregations: ReturnType<typeof useTableData>['state']['aggregations']
  ) => void;
  handleColumnVisibilityChange: (columnId: string, visible: boolean) => void;
  handleColumnReorder: (fromColumnId: string, toColumnId: string) => void;
  handleColumnResize: (columnId: string, width: number) => void;
  handleColumnPin: (columnId: string, side: 'left' | 'right') => void;
  handleColumnUnpin: (columnId: string) => void;
  handlePageChange: (page: number) => void;
  handlePageSizeChange: (pageSize: number) => void;
  handleRetry: () => Promise<void>;
  handleExport: (options: { scope: 'page' | 'selection' | 'all'; format: 'csv' }) => void;
  handleSavedViewSelect: (viewId: string) => void;
  handleSaveView: (name: string) => void;
  handleSavedViewDelete: (viewId: string) => void;
  handleCreateAction: (resolved: ResolvedAction) => void;
  handlePageAction: (resolved: ResolvedAction) => void;
  handleBulkAction: (resolved: ResolvedAction, selectedIds: string[]) => void;
  handleRowAction: (
    resolved: ResolvedAction,
    context: Pick<TableActionContext, 'source' | 'row'>
  ) => void;
  handleToggleRowSelection: (id: string) => void;
  handleSelectAll: (checked: boolean) => void;
  handleClearSelection: () => void;
  handleResetFilters: () => void;
  handleResetLayout: () => void;
  handleResetAll: () => void;
  handleRowActivate: (row: Record<string, unknown>, resolvedAction?: ResolvedAction) => void;
  buildActionContext: (
    source: TableActionContext['source'],
    row?: Record<string, unknown>,
    selectedIds?: string[]
  ) => TableActionContext;
}

export function useTableRenderer(props: TableRendererProps): UseTableRendererResult {
  const {
    contract,
    dataSource,
    data: localData,
    loading: loadingProp,
    error: errorProp,
    onRetry: onRetryProp,
    stale: staleProp,
    degraded: degradedProp,
    onAction,
    rowActivationAction: rowActivationActionProp,
    onRowActivate,
    rowIdField = 'id',
    getRowId,
    detailPath,
    locale,
    showFilterRow = true,
    savedViews: savedViewsProp,
    onSavedViewSelect,
    onSaveView,
    onExport: onExportProp,
  } = props;

  useI18nDebugContext(String(contract.id));
  const { t, i18n } = useTranslation();
  const evalCtx = useEvaluationContext();
  const resolvedLocale = (locale ?? (i18n.language?.startsWith('ne') ? 'ne' : 'en')) as NonNullable<
    TableRendererProps['locale']
  >;

  const savedViewSchemaVersion = contract.persistence?.schemaVersion ?? contract.schemaVersion ?? 1;

  const descriptorSavedViews = useMemo(
    () =>
      (contract.savedViews ?? []).map(view => ({
        id: view.id,
        name: t(resolveTKey(view.name)),
        isDefault: view.isDefault,
        isShared: view.isShared,
        schemaVersion: savedViewSchemaVersion,
        state: view.state as Partial<import('./table-state.js').TableState>,
      })),
    [contract.savedViews, savedViewSchemaVersion, t]
  );

  const [showFilters, setShowFilters] = useState(
    showFilterRow && contract.capabilities?.filtering !== false
  );
  const localSavedViews = useSavedViews({
    tableId: contract.id,
    initialViews: descriptorSavedViews,
    schemaVersion: savedViewSchemaVersion,
  });
  const savedViews = savedViewsProp ?? localSavedViews.views;
  const tablePageSize: number = getGlobalDefaultPageSize() ?? contract.pagination?.pageSize ?? 25;
  const pageSizeOptions: number[] = useMemo(
    () =>
      Array.from(
        new Set([...(contract.pagination?.pageSizeOptions ?? [10, 25, 50]), tablePageSize])
      ).sort((a, b) => a - b),
    [contract.pagination?.pageSizeOptions, tablePageSize]
  );

  const localDataSource = useLocalDataSource(localData ?? []);
  const effectiveDataSource: TableDataSource = useMemo(
    () => dataSource ?? localDataSource,
    [dataSource, localDataSource]
  );

  const getRowIdValue = useCallback(
    (row: Record<string, unknown>): string | undefined => {
      const raw = getRowId ? getRowId(row) : row[contract.rowIdField ?? rowIdField];
      return raw == null ? undefined : String(raw);
    },
    [getRowId, contract.rowIdField, rowIdField]
  );

  const {
    state,
    data: tableData,
    loading: tableLoading,
    error: tableError,
    stale: tableStale,
    degraded: tableDegraded,
    totalCount,
    aggregates,
    groupMetadata,
    referenceDisplayMap,
    actions,
  } = useTableData({
    descriptor: contract,
    dataSource: effectiveDataSource,
    initialData: localData,
    getRowId: getRowIdValue,
  });

  const {
    rowActivationAction,
    handleRowActivate,
    handleCreateAction,
    handlePageAction,
    handleBulkAction,
    handleRowAction,
    buildActionContext,
  } = useTableActions({
    contract,
    state,
    getRowIdValue,
    rowActivationActionProp,
    detailPath,
    onAction,
    onRowActivate,
  });

  const {
    selectionEnabled,
    allPageSelected,
    indeterminate,
    handleToggleRowSelection,
    handleSelectAll,
    handleClearSelection,
  } = useTableSelection({ contract, state, tableData, getRowIdValue, actions });

  const handleExport = useTableExport({
    contract,
    tableData,
    state,
    getRowIdValue,
    onExport: onExportProp,
  });

  const handleSortChange = useCallback(
    (sorting: TableSortState[]) => {
      actions.setSorting(sorting);
    },
    [actions]
  );

  const handleFilterSet = useCallback(
    (filter: TableFilterState) => {
      actions.addFilter(filter);
    },
    [actions]
  );

  const handleFilterRemove = useCallback(
    (field: string) => {
      actions.removeFilter(field);
    },
    [actions]
  );

  const handleGlobalSearchChange = useCallback(
    (search: string) => {
      actions.setGlobalSearch(search);
    },
    [actions]
  );

  const handleToggleFilterRow = useCallback(() => {
    setShowFilters(prev => !prev);
  }, []);

  const handleDensityChange = useCallback(
    (density: 'compact' | 'standard' | 'comfortable') => {
      actions.setDensity(density);
    },
    [actions]
  );

  const handleGroupingChange = useCallback(
    (grouping: ReturnType<typeof useTableData>['state']['grouping']) => {
      actions.setGrouping(grouping);
    },
    [actions]
  );

  const handleAggregationsChange = useCallback(
    (aggregations: ReturnType<typeof useTableData>['state']['aggregations']) => {
      actions.setAggregations(aggregations);
    },
    [actions]
  );

  const handleColumnVisibilityChange = useCallback(
    (columnId: string, visible: boolean) => {
      actions.setColumnVisibility(columnId, visible);
    },
    [actions]
  );

  const handleColumnReorder = useCallback(
    (fromColumnId: string, toColumnId: string) => {
      const newOrder = [...state.columnOrder];
      const fromIdx = newOrder.findIndex(co => co.columnId === fromColumnId);
      const toIdx = newOrder.findIndex(co => co.columnId === toColumnId);
      if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;
      const [removed] = newOrder.splice(fromIdx, 1);
      newOrder.splice(toIdx, 0, removed);
      actions.setColumnOrder(newOrder.map(co => co.columnId));
    },
    [state.columnOrder, actions]
  );

  const handleColumnResize = useCallback(
    (columnId: string, width: number) => {
      actions.setColumnSize(columnId, width);
    },
    [actions]
  );

  const handleColumnPin = useCallback(
    (columnId: string, side: 'left' | 'right') => {
      const col = contract.columns.find(c => c.id === columnId);
      if (!col?.pinnable) return;
      const left = state.pinnedColumns.left.filter(id => id !== columnId);
      const right = state.pinnedColumns.right.filter(id => id !== columnId);
      if (side === 'left') {
        left.push(columnId);
      } else {
        right.push(columnId);
      }
      actions.setPinnedColumns(left, right);
    },
    [contract.columns, state.pinnedColumns, actions]
  );

  const handleColumnUnpin = useCallback(
    (columnId: string) => {
      actions.setPinnedColumns(
        state.pinnedColumns.left.filter(id => id !== columnId),
        state.pinnedColumns.right.filter(id => id !== columnId)
      );
    },
    [state.pinnedColumns, actions]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      actions.goToPage(page);
    },
    [actions]
  );

  const handlePageSizeChange = useCallback(
    (pageSize: number) => {
      actions.setPageSize(pageSize);
    },
    [actions]
  );

  // Track current page size for preference updates
  const currentPageSizeRef = useRef(state.pagination.pageSize);
  currentPageSizeRef.current = state.pagination.pageSize;

  // Listen for preference changes and update page size if the default changed
  usePreferencesPageSize(currentPageSizeRef, handlePageSizeChange);

  const displayLoading = loadingProp ?? tableLoading;
  const displayError = errorProp ?? tableError;
  const displayStale = staleProp ?? tableStale;
  const displayDegraded = degradedProp ?? tableDegraded;
  const currentViewId =
    state.savedViewId ?? (savedViewsProp ? null : localSavedViews.currentViewId);

  const tableMinWidth = useMemo(() => {
    const visibleColumns = getVisibleOrderedColumns(contract, state);
    const columnsWidth = visibleColumns.reduce((acc, column) => {
      const resolvedWidth = state.columnSizes.find(s => s.columnId === column.id)?.width;
      return acc + getColumnIntrinsicMinWidth(column, resolvedWidth);
    }, 0);
    const actionsWidth = (contract.rowActions ?? []).length > 0 ? tableTokens.actionColumn.minWidth : 0;
    const selectionWidth = contract.capabilities?.selection === true ? 48 : 0;
    return columnsWidth + actionsWidth + selectionWidth;
  }, [contract, state]);

  const handleRetry = useCallback(async () => {
    if (onRetryProp) {
      onRetryProp();
      return;
    }
    await actions.refresh();
  }, [onRetryProp, actions]);

  const handleSavedViewSelect = useCallback(
    (viewId: string) => {
      const selectedView = savedViewsProp
        ? savedViews.find(view => view.id === viewId)
        : localSavedViews.selectView(viewId);

      if (selectedView?.state) {
        actions.applySavedView(
          createSavedViewStatePatch(contract, selectedView.state, selectedView.id)
        );
      }

      onSavedViewSelect?.(viewId);
    },
    [savedViewsProp, savedViews, localSavedViews, actions, contract, onSavedViewSelect]
  );

  const handleSaveView = useCallback(
    (name: string) => {
      if (!savedViewsProp) {
        localSavedViews.saveView(
          name,
          createSavedViewStatePatch(contract, state, state.savedViewId)
        );
      }
      onSaveView?.(name);
    },
    [savedViewsProp, localSavedViews, contract, state, onSaveView]
  );

  const handleSavedViewDelete = useCallback(
    (viewId: string) => {
      if (!savedViewsProp) {
        localSavedViews.deleteView(viewId);
      }
    },
    [localSavedViews, savedViewsProp]
  );

  const handleResetFilters = useCallback(() => {
    actions.clearFilters();
    actions.setGlobalSearch('');
  }, [actions]);

  const handleResetLayout = useCallback(() => {
    actions.resetLayout();
  }, [actions]);

  const handleResetAll = useCallback(() => {
    actions.resetAll();
  }, [actions]);

  const pinnedLeft = state.pinnedColumns.left;
  const pinnedRight = state.pinnedColumns.right;
  const showPagination = contract.capabilities?.pagination !== false && totalCount > 0;

  return {
    contract,
    evalCtx,
    resolvedLocale,
    state,
    tableData,
    totalCount,
    aggregates,
    groupMetadata,
    referenceDisplayMap,
    actions,
    displayLoading,
    displayError,
    displayStale,
    displayDegraded,
    showFilters,
    setShowFilters,
    savedViews,
    currentViewId,
    pageSizeOptions,
    rowActivationAction,
    selectionEnabled,
    allPageSelected,
    indeterminate,
    pinnedLeft,
    pinnedRight,
    showPagination,
    tableMinWidth,
    handleSortChange,
    handleFilterSet,
    handleFilterRemove,
    handleGlobalSearchChange,
    handleToggleFilterRow,
    handleDensityChange,
    handleGroupingChange,
    handleAggregationsChange,
    handleColumnVisibilityChange,
    handleColumnReorder,
    handleColumnResize,
    handleColumnPin,
    handleColumnUnpin,
    handlePageChange,
    handlePageSizeChange,
    handleRetry,
    handleExport,
    handleSavedViewSelect,
    handleSaveView,
    handleSavedViewDelete,
    handleCreateAction,
    handlePageAction,
    handleBulkAction,
    handleRowAction,
    handleToggleRowSelection,
    handleSelectAll,
    handleClearSelection,
    handleResetFilters,
    handleResetLayout,
    handleResetAll,
    handleRowActivate,
    buildActionContext,
  };
}
